/**
 * L-14 (TyC §15) — "Exportar mis datos": un botón por conjunto que el tenant puede
 * descargar (lo decide el backend según sus módulos, GET /export). Se usa en la
 * pantalla de suscripción suspendida (30 días) y en Configuración con la cuenta activa.
 * Solo SUPER_USER (la API lo exige; el caller decide el mount).
 */
import { useEffect, useState } from 'react';
import { api, apiDownload } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';

/** V217 (§1ter): `allowed` = false en los conjuntos que solo descarga el titular (clientes, todo). */
interface ExportItem { key: string; label: string; file: string; kind: 'csv' | 'zip'; allowed?: boolean }

/** "Descargar todo" (GET /export/todo.zip): todos los conjuntos del manifiesto en un ZIP. */
const TODO: ExportItem = { key: 'todo', label: 'Descargar todo', file: 'todo.zip', kind: 'zip' };

export function ExportDataPanel({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth() as any;
  const isOwner = !!user?.isOwner;
  const [items, setItems] = useState<ExportItem[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<ExportItem[]>('/export', { silentError: true })
      .then((r) => { if (!cancelled) setItems(r); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  const download = async (it: ExportItem) => {
    setBusy(it.key); setErr(null);
    try {
      const day = new Date().toISOString().slice(0, 10);
      await apiDownload(`/export/${it.file}`, `fluidsol_${it.key}_${day}.${it.kind}`);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo descargar el archivo.');
    } finally { setBusy(null); }
  };

  if (items === null) return <p className="text-xs text-slate-400">Cargando…</p>;
  if (!items.length) return null;

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
      {/* Un solo ZIP con todos los conjuntos de abajo (mismos archivos). V217: solo el titular. */}
      {!isOwner && (
        <p className="text-xs text-slate-500">
          "Descargar todo" y los clientes solo los descarga el titular de la cuenta{user?.ownerName ? ` (${user.ownerName})` : ''}.
        </p>
      )}
      <button
        onClick={() => download(TODO)} disabled={busy !== null || !isOwner}
        className="flex w-full items-center justify-between gap-3 rounded-xl bg-slate-900 px-4 py-2.5 text-left text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
      >
        <span>{TODO.label}</span>
        <span className="shrink-0 text-xs uppercase text-slate-300">{busy === TODO.key ? 'Descargando…' : 'zip'}</span>
      </button>
      <ul className={compact ? 'space-y-1.5' : 'grid gap-2 sm:grid-cols-2'}>
        {items.map((it) => (
          <li key={it.key}>
            <button
              onClick={() => download(it)} disabled={busy !== null || it.allowed === false}
              className="flex w-full items-center justify-between gap-3 rounded-xl bg-white px-4 py-2.5 text-left text-sm text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50"
            >
              <span>{it.label}</span>
              <span className="shrink-0 text-xs uppercase text-slate-400">
                {busy === it.key ? 'Descargando…' : it.kind}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {err && <p className="text-sm text-rose-600">{err}</p>}
    </div>
  );
}

/** Sección de Configuración (cuenta activa). */
export function ExportDataSection() {
  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Exportar mis datos</h3>
        <p className="text-sm text-slate-500">
          Descarga tus clientes, ventas, productos, reservas y facturas en CSV (abre en Excel) o ZIP. Cada descarga se registra y se avisa por correo al titular de la cuenta.
        </p>
      </div>
      <div className="px-6 py-5 md:px-8">
        <ExportDataPanel />
      </div>
    </div>
  );
}
